// Monitor routing for the work drag kernel (W1 Track A).
//
// ONE monitorForElements on the controller root routes every container —
// same-container reorder and cross-container move resolve through the same
// code path (dossier F1). These helpers turn p-dnd's monitor feedback into a
// move descriptor, rejecting what the kernel must ignore: self-drops and any
// drag that does not carry the work ITEM_TYPE (the coexistence contract with
// ui--draggable-list / ui--tag-input-sortable on a shared page).

import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"

// Distinct from draggable_list's "ui-draggable-list-item" and tag_input's
// "ui-tag-input-chip" — the ONLY sanctioned coexistence mechanism (anti-idea 8).
export const ITEM_TYPE = "ui--work-drag-item"

// True when a p-dnd source belongs to the work kernel — the canMonitor /
// canDrop / canScroll filter every attachment installs.
export function isWorkDragSource(source) {
  return source?.data?.type === ITEM_TYPE
}

// Route one monitor drop to a move descriptor, or `null` when the drop is not
// an intent the kernel should publish:
//
// - no drop target (drag ended outside everything) → null — this is also the
//   shape p-dnd hands the monitor for a CANCELLED gesture, so a cancel never
//   produces a drop payload;
// - a foreign ITEM_TYPE → null (never happens past canDrop, but the router is
//   the single enforcement point and is unit-pinned on it);
// - a self-drop (the item dropped onto itself) → null, like draggable_list.
//
// The descriptor carries the neighbour ids UNRESOLVED — `target_id` + `edge` —
// because the slot depends on the target container's live order (rank.js).
// `edge` is null for container-area drops (append semantics).
export function resolveMove({ source, target }) {
  if (!isWorkDragSource(source)) return null
  if (!target || target.data?.type !== ITEM_TYPE) return null

  const itemId = source.data.item_id
  const targetItemId = target.data.item_id ?? null
  if (targetItemId && targetItemId === itemId) return null

  return {
    kind: targetItemId ? "item" : "container",
    from_container: source.data.container_id ?? null,
    container_id: target.data.container_id ?? null,
    item_id: itemId,
    target_id: targetItemId,
    edge: extractClosestEdge(target.data)
  }
}
